import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Lock, LogIn } from "lucide-react";
import { useAuth } from "@/AuthContext";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}`);
      const dest = user.role === "admin" ? "/admin" : "/provider/dashboard";
      navigate(location.state?.from || dest);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white">
        <div />
        <div>
          <div className="text-xs uppercase tracking-widest text-orange-300">For providers & admins</div>
          <h1 className="font-display font-extrabold text-4xl mt-2 leading-tight">Manage your listing.<br/>Track your leads.</h1>
          <p className="mt-4 text-slate-400 max-w-md">
            Provider and admin portal for the Strategic Tracking directory.
            Edit your listing, review buyer enquiries and access provider analytics.
          </p>
        </div>
        <div className="text-xs text-slate-500 font-mono">v1.0</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-md" data-testid="login-form">
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Sign in</div>
          <h2 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mt-2">Use the email associated with your provider or admin account.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Email</Label>
              <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required data-testid="login-email"/>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</Label>
                <Link to="/forgot-password" className="text-xs font-semibold text-[#002FA7] hover:underline" data-testid="login-forgot-link">Forgot password?</Link>
              </div>
              <Input type="password" value={password} onChange={(e)=>setPassword(e.target.value)} required data-testid="login-password"/>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center h-11" data-testid="login-submit">
              <LogIn size={16}/> {submitting ? "Signing in…" : "Sign in"}
            </button>
          </div>

          <div className="mt-6 text-sm text-slate-600">
            New provider? <Link to="/register" className="text-[#002FA7] font-semibold hover:underline" data-testid="login-register-link">Create an account</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
